import {
  Component,
  type OnInit,
  type OnDestroy,
  ChangeDetectionStrategy,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';

import { AuthenticationService } from '../../auth/authentication.service';
import { SharedService } from 'src/app/shared/services/shared.service';
import { SpinnerComponent } from 'src/app/shared/components/spinner/spinner.component';
import { FooterComponent } from 'src/app/shared/components/footer/footer.component';
import { LOGIN_LABELS } from './login.labels';
import { LoginI } from '../../auth/authentication.interface';

@Component({
  selector: 'app-login',
  standalone: true,
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatToolbarModule,
    MatIconModule,
    SpinnerComponent,
    FooterComponent
  ]
})
export class LoginComponent implements OnInit, OnDestroy {
  labels = LOGIN_LABELS;

  public loginForm!: FormGroup;
  public isLoading = false;
  public hidePassword = true;

  private destroy$ = new Subject<void>();

  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  private authService = inject(AuthenticationService);
  private sharedService = inject(SharedService);
  private spinner = inject(NgxSpinnerService);

  ngOnInit(): void {
    this.initializeForm();
    this.clearSessionData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      Pwp: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  private clearSessionData(): void {
    sessionStorage.removeItem('token');
    localStorage.removeItem('b2c');
  }

  public hasFieldError(fieldName: string, errorType: string): boolean {
    const field = this.loginForm.get(fieldName);
    return !!(field && field.hasError(errorType) && (field.dirty || field.touched));
  }

  public onLogin(): void {
    if (this.loginForm.valid && !this.isLoading) {
      this.isLoading = true;
      this.spinner.show();

      const credentials: LoginI = {
        email: this.loginForm.get('email')?.value,
        Pwp: this.loginForm.get('Pwp')?.value
      };

      this.authService
        .login(credentials)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.router.navigate(['/wapi/home']);
          },
          error: (err: unknown) => {
            this.sharedService.showErrorMessage(
              err instanceof Error ? err.message : 'Login failed. Please try again.'
            );
          }
        })
        .add(() => {
          this.isLoading = false;
          this.spinner.hide();
        });
    } else {
      this.markFormGroupTouched();
    }
  }

  public onB2CLogin(): void {
    this.sharedService.B2CLogin();
  }

  public togglePasswordVisibility(): void {
    this.hidePassword = !this.hidePassword;
  }

  private markFormGroupTouched(): void {
    Object.keys(this.loginForm.controls).forEach(key => {
      const control = this.loginForm.get(key);
      control?.markAsTouched();
    });
  }

  public isFieldInvalid(fieldName: string): boolean {
    const field = this.loginForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }
}
